<script setup lang="ts">
import { faPlane, faHotel } from '@fortawesome/pro-solid-svg-icons';
import { ReserveType } from '@utils/itinerary';

const { margin, inFilters, mobileOnly, desktopOnly } = defineProps<IProps>();
const itineraryStore = useItineraryStore();
const searchFormStore = useSearchFormStore();
const currentModule = useCurrentItineraryModule();
interface IProps {
    mobileOnly?: boolean;
    desktopOnly?: boolean;
    margin?: string;
    inFilters?: boolean;
}
const isLoading = computed(
    () =>
        currentModule.value.isRealTimeLoading &&
        !itineraryStore.allPackagesLoaded
);
const info = computed(() => {
    let title;
    let icon;
    switch (searchFormStore.reserveType) {
        case ReserveType.Hotel:
            title = 'در حال جستجوی هتل‌ها...';
            icon = faHotel;
            break;
        case ReserveType.Flight:
        default:
            title = 'در حال جستجوی پروازها...';
            icon = faPlane;
            break;
    }
    return {
        title: inFilters ? 'در حال جستجو...' : title,
        icon,
    };
});
</script>

<template>
    <div
        v-show="isLoading"
        class="real-time-loading"
        :style="{ margin }"
        :class="{
            desktop: desktopOnly,
            mobile: mobileOnly,
            'in-filters': inFilters,
        }"
    >
        <div class="icon-animation-container">
            <div class="circle-animation" />
            <mr-icon
                class="icon"
                :icon="info.icon"
                color="var(--blue-400)"
                size="x-small"
            />
        </div>
        <div class="loading-title">
            {{ info.title }}
        </div>
    </div>
</template>

<style lang="scss" scoped>
$animation-duration: 1.8s;
.real-time-loading {
    display: flex;
    align-items: center;
    padding: 0 8px;
    @include mobile-only {
        &.desktop {
            display: none;
        }
    }
    @include desktop-only {
        &.mobile {
            display: none;
        }
    }
    &.in-filters {
        .loading-title {
            @include tg-s($w7, white);
        }
    }
}

.icon-animation-container {
    position: relative;
    width: 36px;
    height: 36px;
    overflow: hidden;
}

.circle-animation {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 36px;
    height: 36px;
    animation: loader-animation $animation-duration infinite ease-in-out;
    animation-delay: 100ms;
    border-radius: 100%;
}

.icon {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translateY(-50%);
    animation: icon-animation $animation-duration infinite ease-in-out;
    animation-delay: 100ms;
}

@keyframes loader-animation {
    0% {
        background-color: usePalette($p-cool-gray, 200);
        scale: 1;
    }
    20%,
    80% {
        background-color: usePalette($p-blue, 200);
        scale: 0.888888889;
    }

    100% {
        background-color: usePalette($p-cool-gray, 200);
        scale: 1;
    }
}

@keyframes icon-animation {
    0% {
        transform: translate(-200%, -50%);
    }
    30%,
    70% {
        transform: translate(-50%, -50%);
    }
    100% {
        transform: translate(100%, -50%);
    }
}

.loading-title {
    @include tg-m($w5, usePalette($p-cool-gray, $p-600));
    margin-right: 8px;
    white-space: nowrap;
}
</style>
